import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaTruck, FaFileAlt, FaFileInvoice, FaCheckCircle, FaPlusCircle, FaArrowRight,
  FaSpinner, FaMapMarkerAlt, FaGlobeAfrica, FaUserTie, FaEnvelope, FaPhone,
  FaQuestionCircle, FaBuilding, FaRobot, FaSyncAlt, FaProjectDiagram, FaChartLine
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Tooltip, Legend, Filler
} from 'chart.js';
import ClientLayout from '../layouts/ClientLayout';
import InviteTeamNudge from '../components/InviteTeamNudge';
import StatCard from '../components/StatCard';
import { clientDashboard } from '../api';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler
);

const fmtMoney = (n) => 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtMoneyExact = (n) => 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const JOB_STATUS = {
  new: { label: 'New', color: '#0A1929', bg: '#f1f5f9' },
  pending: { label: 'Pending', color: '#b45309', bg: '#fffbeb' },
  active: { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  in_progress: { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  scheduled: { label: 'Scheduled', color: '#7c3aed', bg: '#f5f3ff' },
  accepted: { label: 'Scheduled', color: '#7c3aed', bg: '#f5f3ff' },
  completed: { label: 'Completed', color: '#16a34a', bg: '#f0fdf4' },
  delivered: { label: 'Delivered', color: '#16a34a', bg: '#f0fdf4' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' }
};

const QUOTE_STATUS = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9' },
  client_request: { label: 'Pending Review', color: '#b45309', bg: '#fffbeb' },
  sent: { label: 'Awaiting You', color: '#2563eb', bg: '#eff6ff' },
  client_accepted: { label: 'Accepted', color: '#16a34a', bg: '#f0fdf4' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' }
};

const INVOICE_STATUS = {
  proforma: { label: 'Proforma', color: '#b45309', bg: '#fffbeb' },
  pending: { label: 'Awaiting Payment', color: '#b45309', bg: '#fffbeb' },
  sent: { label: 'Sent', color: '#2563eb', bg: '#eff6ff' },
  partially_paid: { label: 'Part Paid', color: '#b45309', bg: '#fffbeb' },
  paid: { label: 'Paid', color: '#16a34a', bg: '#f0fdf4' },
  overdue: { label: 'Overdue', color: '#dc2626', bg: '#fef2f2' },
  credited: { label: 'Credited', color: '#6b7280', bg: '#f3f4f6' },
  void: { label: 'Voided', color: '#6b7280', bg: '#f3f4f6' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' }
};

const POD_STATUS = {
  pending: { label: 'Awaiting Approval', color: '#b45309', bg: '#fffbeb' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4' },
  received: { label: 'Received', color: '#16a34a', bg: '#f0fdf4' },
  rejected: { label: 'Query Raised', color: '#dc2626', bg: '#fef2f2' }
};

const QUOTE_TYPES = {
  minerals: '⛏️ Minerals', agri: '🌾 Agri', abnormal: '🚛 Abnormal',
  container: '📦 Container', general: '📋 General'
};

const TONES = {
  blue: { bar: '#2563eb', bg: '#eff6ff', text: '#1d4ed8' },
  amber: { bar: '#d97706', bg: '#fffbeb', text: '#b45309' },
  red: { bar: '#dc2626', bg: '#fef2f2', text: '#b91c1c' },
  green: { bar: '#00A86B', bg: '#f0fdf4', text: '#047857' }
};

const Pill = ({ meta, fallback }) => (
  <span
    className="px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
    style={{ color: (meta && meta.color) || '#64748b', backgroundColor: (meta && meta.bg) || '#f1f5f9' }}
  >
    {(meta && meta.label) || fallback || '—'}
  </span>
);

const Card = ({ title, action, children, className = '', bodyClass = '' }) => (
  <div className={`bg-white rounded-xl border ${className}`} style={{ borderColor: '#E8ECEF' }}>
    {title && (
      <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E8ECEF' }}>
        <h3 className="font-semibold" style={{ color: '#0A1929' }}>{title}</h3>
        {action}
      </div>
    )}
    <div className={bodyClass}>{children}</div>
  </div>
);

function ClientDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await clientDashboard.get();
        if (cancelled) return;
        if (res && res.success) setData(res);
        else setError((res && res.error) || 'Could not load your dashboard.');
      } catch (err) {
        console.error('Dashboard error:', err);
        if (!cancelled) setError('Could not load your dashboard. Please refresh the page.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Jeeves ──────────────────────────────────────────────────────────────
  // His briefing is written once a day per client and then cached, so asking for it on every page
  // load costs nothing. If he is unavailable the card falls back to the figures already on this
  // page — never an error, never an empty box.
  const [jeeves, setJeeves] = useState(null);
  const [jeevesLoading, setJeevesLoading] = useState(false);
  const [jeevesNote, setJeevesNote] = useState('');

  const loadJeeves = async (regenerate = false) => {
    setJeevesLoading(true);
    setJeevesNote('');
    try {
      const res = await clientDashboard.brief(regenerate);
      if (res && res.success && res.jeeves) setJeeves(res.jeeves);
      else setJeevesNote((res && res.error) || '');
    } catch (err) {
      console.warn('Briefing unavailable:', err);
      setJeevesNote('The briefing is unavailable at the moment.');
    } finally {
      setJeevesLoading(false);
    }
  };

  useEffect(() => { loadJeeves(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <ClientLayout activeTab="dashboard" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-4" style={{ color: '#00A86B' }} />
            <p className="text-gray-500">Loading your dashboard...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  const profile = (data && data.profile) || {};
  const kpis = (data && data.kpis) || {};
  const attention = (data && data.attention) || [];
  const recentQuotes = (data && data.recent_quotes) || [];
  const recentJobs = (data && data.recent_jobs) || [];
  const recentInvoices = (data && data.recent_invoices) || [];
  const pods = (data && data.pods) || [];
  const firstName = profile.user_name || user.full_name || 'there';
  const monthly = (data && data.monthly) || [];
  const projects = (data && data.projects) || [];

  // Month on month change in what they were billed. Null when there is nothing to compare with —
  // better a plain figure than a meaningless "+100%" against zero.
  const billedChange = (() => {
    const now = Number(kpis.billed_this_month) || 0;
    const before = Number(kpis.billed_last_month) || 0;
    if (before <= 0) return null;
    return Math.round(((now - before) / before) * 1000) / 10;
  })();

  // Two series on one chart: what they were billed (money, left axis) and what we moved for them
  // (loads, right axis). The counts are deliberately on their own axis — a load count and a rand
  // value on the same scale would make one of them invisible.
  const monthlyChart = {
    labels: monthly.map((m) => m.label),
    datasets: [
      {
        label: 'Billed',
        data: monthly.map((m) => m.billed),
        borderColor: '#00A86B',
        backgroundColor: 'rgba(0,168,107,0.10)',
        fill: true,
        tension: 0.35,
        pointRadius: 2,
        borderWidth: 2,
        yAxisID: 'y'
      },
      {
        label: 'Loads delivered',
        data: monthly.map((m) => m.loads),
        borderColor: '#3B82F6',
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.35,
        yAxisID: 'y1'
      }
    ]
  };

  const monthlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0A1929',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => (ctx.datasetIndex === 0
            ? `Billed ${fmtMoney(ctx.parsed.y)}`
            : `${ctx.parsed.y} load${ctx.parsed.y === 1 ? '' : 's'} delivered`)
        }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#94A3B8', font: { size: 10 } } },
      y: {
        position: 'left',
        grid: { color: '#F1F5F9' },
        border: { display: false },
        ticks: {
          color: '#94A3B8',
          font: { size: 10 },
          callback: (v) => (Math.abs(v) >= 1000 ? `R${Math.round(v / 1000)}k` : `R${v}`)
        }
      },
      y1: {
        position: 'right',
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#94A3B8', font: { size: 10 }, precision: 0 }
      }
    }
  };

  const statCards = [
    {
      key: 'jobs', to: '/client-jobs', icon: <FaTruck size={16} />, tint: '#2563eb',
      label: 'Active jobs', value: kpis.active_jobs || 0,
      sub: (kpis.completed_jobs || 0) > 0 ? `${kpis.completed_jobs} completed` : 'Nothing delivered yet'
    },
    {
      key: 'quotes', to: '/client-quotes', icon: <FaFileAlt size={16} />, tint: '#00A86B',
      label: 'Quotes awaiting you', value: kpis.quotes_awaiting || 0,
      sub: (kpis.quotes_awaiting_value || 0) > 0 ? `${fmtMoney(kpis.quotes_awaiting_value)} on the table` : 'Nothing outstanding'
    },
    {
      key: 'money', to: '/client-invoices', icon: <FaFileInvoice size={16} />, tint: (kpis.overdue_count || 0) > 0 ? '#dc2626' : '#0A1929',
      label: 'Outstanding balance', value: fmtMoney(kpis.outstanding_amount || 0),
      sub: (kpis.overdue_count || 0) > 0
        ? `${kpis.overdue_count} overdue`
        : ((kpis.paid_to_date || 0) > 0 ? `${fmtMoney(kpis.paid_to_date)} paid to date` : 'No invoices yet')
    },
    {
      key: 'pods', to: '/client-pods', icon: <FaCheckCircle size={16} />, tint: '#7c3aed',
      label: 'Proofs of delivery', value: kpis.pods_available || 0,
      sub: (kpis.on_time_sample || 0) > 0 ? `${kpis.on_time_rate}% on-time deliveries` : 'Signed PODs appear here'
    }
  ];

  return (
    <ClientLayout activeTab="dashboard" onLogout={onLogout} user={user}>
      {/* ── Greeting ── */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>👋 Welcome back, {firstName}!</h1>
          <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
            {profile.company_name || 'Your logistics overview'}
            {profile.member_since ? ` · customer since ${profile.member_since}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/client-help" className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50" style={{ borderColor: '#E8ECEF', color: '#0A1929' }}>
            <FaQuestionCircle size={13} /> Help
          </Link>
          <Link to="/client-new-job" className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#00A86B' }}>
            <FaPlusCircle size={14} /> Request a quote
          </Link>
        </div>
      </div>

      <InviteTeamNudge />

      {error ? (
        <div className="rounded-xl border p-4 mb-6 text-sm" style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2', color: '#b91c1c' }}>{error}</div>
      ) : null}

      {/* ── Needs your attention ── */}
      {attention.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {attention.map((a) => {
            const tone = TONES[a.tone] || TONES.blue;
            return (
              <div key={a.key} className="rounded-xl border p-4 flex items-start gap-3" style={{ borderColor: '#E8ECEF', backgroundColor: tone.bg }}>
                <span className="w-1.5 rounded-full self-stretch flex-shrink-0" style={{ backgroundColor: tone.bar }} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm" style={{ color: '#0A1929' }}>{a.title}</p>
                  <p className="text-xs mt-1" style={{ color: '#4A5568' }}>{a.detail}</p>
                  {a.href ? (
                    <button onClick={() => navigate(a.href)} className="text-xs font-semibold mt-2 flex items-center gap-1 hover:underline" style={{ color: tone.text }}>
                      {a.cta} <FaArrowRight size={10} />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── At a glance ── */}
      {/* The office dashboard's KPI treatment, so the two portals look like one product. Still
          clickable: each card leads to the page behind its number. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((c) => (
          <StatCard
            key={c.key}
            label={c.label}
            value={c.value}
            sub={c.sub}
            icon={c.icon}
            accent={c.tint}
            onClick={() => navigate(c.to)}
          />
        ))}
      </div>

      {/* ── Jeeves ── */}
      {/* Same brief for the whole day (one paid call per client per day, cached), so Regenerate is
          deliberately the only way to ask for another — and the server caps those. */}
      {(jeeves || jeevesLoading || jeevesNote) && (
        <div className="rounded-xl overflow-hidden border mb-6" style={{ borderColor: '#E8ECEF' }}>
          <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-3"
            style={{ background: 'linear-gradient(120deg, #0A1929 0%, #12372A 55%, #00A86B 100%)' }}>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#FFFFFF' }}>
                <FaRobot size={16} />
              </span>
              <div>
                <p className="text-sm font-bold text-white">Jeeves</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.72)' }}>Your account, at a glance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {jeeves && jeeves.mood && (
                <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#FFFFFF' }}>
                  {jeeves.mood}
                </span>
              )}
              <button onClick={() => loadJeeves(true)} disabled={jeevesLoading}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-60"
                style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#FFFFFF' }}>
                <FaSyncAlt className={jeevesLoading ? 'animate-spin' : ''} size={11} />
                {jeevesLoading ? 'Consulting…' : 'Regenerate'}
              </button>
            </div>
          </div>
          <div className="bg-white p-5">
            {jeeves ? (
              <>
                <p className="text-sm leading-relaxed" style={{ color: '#0A1929' }}>{jeeves.summary}</p>
                {(jeeves.observations || []).length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {jeeves.observations.map((o, i) => (
                      <li key={i} className="text-xs flex items-start gap-2" style={{ color: '#4A5568' }}>
                        <span style={{ color: '#00A86B' }}>·</span>{o}
                      </li>
                    ))}
                  </ul>
                )}
                {(jeeves.actions || []).length > 0 && (
                  <div className="mt-4 pt-3 border-t space-y-1.5" style={{ borderColor: '#F1F5F9' }}>
                    {(jeeves.actions || []).map((a, i) => (
                      <p key={i} className="text-xs font-medium flex items-start gap-2" style={{ color: '#0A1929' }}>
                        <FaArrowRight size={9} className="mt-1 flex-shrink-0" style={{ color: '#00A86B' }} />{a}
                      </p>
                    ))}
                  </div>
                )}
              </>
            ) : (
              // No AI, no fuss: the same story told in figures taken from this very page.
              <div>
                <p className="text-sm" style={{ color: '#4A5568' }}>
                  {jeevesNote || 'Your briefing is being prepared.'}
                </p>
                <ul className="mt-3 space-y-1.5">
                  <li className="text-xs" style={{ color: '#4A5568' }}>· Quotes awaiting you: <strong>{kpis.quotes_awaiting || 0}</strong></li>
                  <li className="text-xs" style={{ color: '#4A5568' }}>· Jobs in progress: <strong>{kpis.active_jobs || 0}</strong>, completed <strong>{kpis.completed_jobs || 0}</strong></li>
                  <li className="text-xs" style={{ color: '#4A5568' }}>· Outstanding balance: <strong>{fmtMoney(kpis.outstanding_amount || 0)}</strong></li>
                  {(kpis.project_tasks_waiting || 0) > 0 && (
                    <li className="text-xs" style={{ color: '#4A5568' }}>· Project tasks waiting on you: <strong>{kpis.project_tasks_waiting}</strong></li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Spend and deliveries ── */}
      {monthly.length > 0 && (
        <Card
          className="mb-6"
          title="Your spend and deliveries"
          bodyClass="p-5"
          action={(
            <span className="text-xs" style={{ color: '#64748B' }}>
              Billed this month <strong style={{ color: '#0A1929' }}>{fmtMoney(kpis.billed_this_month || 0)}</strong>
              {billedChange != null ? ` (${billedChange >= 0 ? '▲' : '▼'} ${Math.abs(billedChange)}% vs last month)` : ''}
            </span>
          )}
        >
          <div style={{ height: 240 }}>
            <Line data={monthlyChart} options={monthlyOptions} />
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-xs" style={{ color: '#64748B' }}>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 rounded" style={{ backgroundColor: '#00A86B' }} /> Billed (as invoiced)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 rounded" style={{ backgroundColor: '#3B82F6' }} /> Loads delivered
            </span>
          </div>
        </Card>
      )}

      {/* ── Projects ── */}
      {projects.length > 0 && (
        <Card
          className="mb-6"
          title="Your projects"
          bodyClass="p-5"
          action={(
            <Link to="/client-projects" className="text-xs font-semibold flex items-center gap-1 hover:underline" style={{ color: '#00A86B' }}>
              View all <FaArrowRight size={10} />
            </Link>
          )}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/client-projects/${p.id}`)}
                className="text-left rounded-xl border p-4 hover:shadow-sm transition-shadow"
                style={{ borderColor: '#E8ECEF' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: '#0A1929' }}>{p.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>
                      {[p.project_number, p.status, p.end_date ? `due ${fmtDate(p.end_date)}` : null].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {p.tasks_waiting_on_you > 0 && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: '#fffbeb', color: '#b45309' }}>
                      {p.tasks_waiting_on_you} on you
                    </span>
                  )}
                </div>

                {p.progress != null && (
                  <>
                    <div className="h-2 rounded-full mt-3 overflow-hidden" style={{ backgroundColor: '#F1F5F9' }}>
                      <div className="h-2 rounded-full" style={{ width: `${Math.min(100, p.progress)}%`, backgroundColor: '#00A86B' }} />
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#64748B' }}>{p.progress}% through</p>
                  </>
                )}

                <p className="text-xs mt-2" style={{ color: '#4A5568' }}>
                  {p.tasks_total} task{p.tasks_total === 1 ? '' : 's'} · {p.tasks_open} open
                  {p.next_task ? ` · next: ${p.next_task.name}${p.next_task.end_date ? ` (due ${fmtDate(p.next_task.end_date)})` : ''}` : ''}
                </p>
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* ── Quotes ── */}
        <Card
          className="lg:col-span-2"
          title="Your quotes"
          action={(
            <Link to="/client-quotes" className="text-xs font-semibold flex items-center gap-1 hover:underline" style={{ color: '#00A86B' }}>
              View all <FaArrowRight size={10} />
            </Link>
          )}
        >
          {recentQuotes.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-4xl mb-3">📄</p>
              <p className="font-semibold text-sm" style={{ color: '#0A1929' }}>No quotes yet</p>
              <p className="text-xs mt-1 mb-4" style={{ color: '#4A5568' }}>Tell us what you need moved and we will price it for you.</p>
              <Link to="/client-new-job" className="inline-block px-5 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#00A86B' }}>
                Request a quote
              </Link>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#F1F5F9' }}>
              {recentQuotes.map((q) => (
                <button
                  key={q.id}
                  onClick={() => navigate(`/client-quotes/${q.id}`)}
                  className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors flex items-start justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm" style={{ color: '#0A1929' }}>{q.quote_number}</span>
                      <Pill meta={QUOTE_STATUS[q.status]} fallback={q.status} />
                      {q.cross_border ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1" style={{ color: '#b45309', backgroundColor: '#fffbeb' }}>
                          <FaGlobeAfrica size={9} /> Cross-border
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs mt-1 flex items-center gap-1 min-w-0" style={{ color: '#4A5568' }}>
                      <FaMapMarkerAlt size={10} style={{ color: '#00A86B' }} />
                      <span className="truncate">{q.origin || '—'} → {q.destination || '—'}</span>
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>
                      {QUOTE_TYPES[q.quote_type] || q.quote_type}
                      {q.sent_at ? ` · sent ${fmtDate(q.sent_at)}` : ''}
                      {q.expiry_date ? ` · valid until ${fmtDate(q.expiry_date)}` : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold" style={{ color: '#0A1929' }}>{fmtMoney(q.total)}</p>
                    <p className="text-[11px]" style={{ color: '#94A3B8' }}>{q.distance_km ? `${q.distance_km} km` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* ── Invoices ── */}
        <Card
          title="Invoices & proformas"
          action={(
            <Link to="/client-invoices" className="text-xs font-semibold flex items-center gap-1 hover:underline" style={{ color: '#00A86B' }}>
              View all <FaArrowRight size={10} />
            </Link>
          )}
        >
          {recentInvoices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-4xl mb-3">🧾</p>
              <p className="font-semibold text-sm" style={{ color: '#0A1929' }}>Nothing invoiced yet</p>
              <p className="text-xs mt-1" style={{ color: '#4A5568' }}>Proformas and invoices appear here as soon as your jobs are billed.</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#F1F5F9' }}>
              {recentInvoices.map((i) => (
                <button key={i.id} onClick={() => navigate('/client-invoices')} className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-sm" style={{ color: '#0A1929' }}>{i.number || i.invoice_number}</span>
                    <Pill meta={INVOICE_STATUS[i.status]} fallback={i.status} />
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs" style={{ color: '#4A5568' }}>
                    <span>
                      {i.kind === 'proforma' ? 'Proforma' : 'Invoice'}
                      {i.due_date ? ` · due ${fmtDate(i.due_date)}` : ''}
                    </span>
                    <span className="font-semibold" style={{ color: i.balance > 0 ? (i.overdue ? '#dc2626' : '#0A1929') : '#16a34a' }}>
                      {i.balance > 0 ? `Due ${fmtMoneyExact(i.balance)}` : `Settled ${fmtMoneyExact(i.total)}`}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* ── Jobs ── */}
        <Card
          title="Jobs in progress"
          action={(
            <Link to="/client-jobs" className="text-xs font-semibold flex items-center gap-1 hover:underline" style={{ color: '#00A86B' }}>
              View all <FaArrowRight size={10} />
            </Link>
          )}
        >
          {recentJobs.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-4xl mb-3">🚚</p>
              <p className="font-semibold text-sm" style={{ color: '#0A1929' }}>No jobs on the road right now</p>
              <p className="text-xs mt-1" style={{ color: '#4A5568' }}>
                Accept a quote and we will create the job — you can track every load here.
              </p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#F1F5F9' }}>
              {recentJobs.map((j) => (
                <button key={j.id} onClick={() => navigate('/client-jobs')} className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm" style={{ color: '#0A1929' }}>{j.job_number}</span>
                      <Pill meta={JOB_STATUS[String(j.status || '').toLowerCase()]} fallback={j.status} />
                      {j.cross_border ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1" style={{ color: '#b45309', backgroundColor: '#fffbeb' }}>
                          <FaGlobeAfrica size={9} /> Cross-border
                        </span>
                      ) : null}
                    </div>
                    <span className="text-xs" style={{ color: '#94A3B8' }}>
                      {j.estimated_end_date ? `ETA ${fmtDate(j.estimated_end_date)}` : fmtDate(j.created_at)}
                    </span>
                  </div>
                  <p className="text-xs mt-1 truncate" style={{ color: '#4A5568' }}>
                    <FaMapMarkerAlt size={10} style={{ color: '#00A86B' }} className="inline mr-1" />
                    {[j.origin, j.destination].filter(Boolean).join(' → ') || 'Route to be confirmed'}
                  </p>
                  {j.commodity ? (
                    <p className="text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>{j.commodity}</p>
                  ) : null}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E8ECEF' }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, j.progress || 0))}%`, backgroundColor: '#00A86B' }} />
                    </div>
                    <span className="text-[11px] font-medium" style={{ color: '#4A5568' }}>{j.progress || 0}%</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* ── PODs ── */}
        <Card
          title="Proof of delivery"
          action={(
            <Link to="/client-pods" className="text-xs font-semibold flex items-center gap-1 hover:underline" style={{ color: '#00A86B' }}>
              Open PODs <FaArrowRight size={10} />
            </Link>
          )}
        >
          {pods.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-4xl mb-3">📝</p>
              <p className="font-semibold text-sm" style={{ color: '#0A1929' }}>No proofs of delivery yet</p>
              <p className="text-xs mt-1" style={{ color: '#4A5568' }}>
                Signed delivery notes appear here as soon as your first load is delivered.
              </p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#F1F5F9' }}>
              {pods.map((p) => (
                <button key={p.id} onClick={() => navigate('/client-pods')} className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-sm" style={{ color: '#0A1929' }}>
                      {p.pod_number || p.load_number || p.job_number || 'POD'}
                    </span>
                    <Pill meta={POD_STATUS[p.status]} fallback={p.status} />
                  </div>
                  <p className="text-xs mt-1 truncate" style={{ color: '#4A5568' }}>{p.route || p.job_number || '—'}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>
                    {p.receiver ? `Signed by ${p.receiver}` : 'Receiver to be confirmed'}
                    {p.weight_mt ? ` · ${p.weight_mt} MT` : ''}
                    {p.delivered_at ? ` · ${fmtDate(p.delivered_at)}` : ''}
                  </p>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" title="Quick actions" bodyClass="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { to: '/client-new-job', label: 'Request a quote', color: '#00A86B' },
              { to: '/client-quotes', label: 'My quotes', color: '#2563eb' },
              { to: '/client-invoices', label: 'Invoices', color: '#0A1929' },
              { to: '/client-pods', label: 'Proof of delivery', color: '#7c3aed' },
              { to: '/client-jobs', label: 'My jobs', color: '#0891b2' },
              { to: '/client-loads', label: 'Loads', color: '#4b5563' },
              { to: '/client-reports', label: 'Reports', color: '#d97706' },
              { to: '/client-help', label: 'Help & tutorials', color: '#16a34a' }
            ].map((a) => (
              <Link
                key={a.to}
                to={a.to}
                className="rounded-xl border p-4 text-center hover:shadow-sm transition-shadow"
                style={{ borderColor: '#E8ECEF' }}
              >
                <span className="block text-sm font-semibold" style={{ color: a.color }}>{a.label}</span>
              </Link>
            ))}
          </div>
        </Card>

        <Card title="Your account" bodyClass="p-5">
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <FaBuilding size={12} className="mt-1 flex-shrink-0" style={{ color: '#94A3B8' }} />
              <div>
                <p className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>Company</p>
                <p style={{ color: '#0A1929' }}>{profile.company_name || '—'}</p>
                {profile.member_since ? (
                  <p className="text-xs" style={{ color: '#4A5568' }}>Customer since {profile.member_since}</p>
                ) : null}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FaUserTie size={12} className="mt-1 flex-shrink-0" style={{ color: '#94A3B8' }} />
              <div>
                <p className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>Company contact</p>
                <p style={{ color: '#0A1929' }}>{profile.primary_contact || '—'}</p>
                {profile.primary_contact_email ? (
                  <p className="text-xs flex items-center gap-1" style={{ color: '#4A5568' }}>
                    <FaEnvelope size={10} /> {profile.primary_contact_email}
                  </p>
                ) : null}
                {profile.primary_contact_phone ? (
                  <p className="text-xs flex items-center gap-1" style={{ color: '#4A5568' }}>
                    <FaPhone size={10} /> {profile.primary_contact_phone}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FaUserTie size={12} className="mt-1 flex-shrink-0" style={{ color: '#00A86B' }} />
              <div>
                <p className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>Signed in as</p>
                <p style={{ color: '#0A1929' }}>{profile.user_full_name || firstName}</p>
                {profile.user_email ? (
                  <p className="text-xs flex items-center gap-1" style={{ color: '#4A5568' }}>
                    <FaEnvelope size={10} /> {profile.user_email}
                  </p>
                ) : null}
                {profile.user_phone ? (
                  <p className="text-xs flex items-center gap-1" style={{ color: '#4A5568' }}>
                    <FaPhone size={10} /> {profile.user_phone}
                  </p>
                ) : (
                  <p className="text-xs" style={{ color: '#b45309' }}>Add your cell number for delivery updates</p>
                )}
              </div>
            </div>

            <Link
              to="/client-profile"
              className="inline-flex items-center gap-2 text-xs font-semibold hover:underline"
              style={{ color: '#00A86B' }}
            >
              Update my details in my profile <FaArrowRight size={10} />
            </Link>
          </div>
        </Card>
      </div>



    </ClientLayout>
  );


}

export default ClientDashboard;
